/* eslint-disable react/style-prop-object */
/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import type { FinancialSegmentsFields, UnwrappedSegment } from "components/stocks/modern/OperatingSegments/types";
import { SimpleTextTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import Twemoji from "components/ui/Twemoji";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import sum from "lodash/sum";
import { HelpCircleIcon } from "lucide-react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

interface OperatingSegmentsListProps {
  kind: "GEOGRAPHIC" | "BUSINESS";
  forYear: number;
  financialSegments: FinancialSegmentsFields;
  segments: UnwrappedSegment[];
}
export default function OperatingSegmentsList({
  kind,
  forYear,
  financialSegments,
  segments,
}: OperatingSegmentsListProps) {
  const intl = useIntl();
  const theme = useTheme();

  const isGeographic = kind === "GEOGRAPHIC";
  const filteredSegments = segments
    .filter((node) => node.name !== "Others")
    .filter((node) => {
      const financialIndicatorHistoryNode = (node.financialIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });
      const ratioIndicatorHistoryNode = (node.ratioIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });

      return financialIndicatorHistoryNode && ratioIndicatorHistoryNode;
    });

  const standardizedCountries = genStandardizedCountries(intl);

  const limitedSegments = filteredSegments
    .slice(0, isGeographic ? 5 : 4)
    .map((node) => {
      const standardizedCountry = standardizedCountries.find((candidate) => {
        return candidate.standardizedName === node.name;
      });
      const financialIndicatorHistoryNode = (node.financialIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });
      const ratioIndicatorHistoryNode = (node.ratioIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });

      if (!financialIndicatorHistoryNode && !ratioIndicatorHistoryNode) {
        return null;
      }

      return {
        name: node.name,
        standardizedCountry,
        isPrimary: node.isPrimary,
        financialIndicator: financialIndicatorHistoryNode?.value || 0,
        ratioIndicator: ratioIndicatorHistoryNode?.value || 0,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => (a.ratioIndicator > b.ratioIndicator ? -1 : 1));

  const restSegments = filteredSegments.slice(5, filteredSegments.length);
  const otherSegment = segments.find((node) => node.name === "Others");

  const totalRemainingRatio =
    [...restSegments, otherSegment].filter(notUndefinedOrNull).reduce((total, node) => {
      const ratioIndicatorHistoryNode = (node.ratioIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });

      if (!ratioIndicatorHistoryNode) {
        return total;
      }

      return total + ratioIndicatorHistoryNode.value;
    }, 0) / 100;

  const hasSubTotal = limitedSegments.length < filteredSegments.length;
  const currency = financialSegments.currency === "BRL" ? "BRL" : "USD";

  return (
    <div className="h-full rounded-2xl border-none border-slate-200 bg-slate-50/80 p-4 dark:bg-slate-800">
      <ul className="divide-y divide-slate-200 dark:divide-slate-700">
        {limitedSegments.map(({ name, standardizedCountry, isPrimary, financialIndicator, ratioIndicator }, index) => {
          const { lineColor } = getColorThemeForIndex(index, theme as "light" | "dark");
          const description = isGeographic && standardizedCountry ? standardizedCountry.name : name;

          return (
            <li
              key={name}
              className={classNames("flex items-center justify-between gap-x-6 py-4", "first:pt-0 last:pb-0")}
            >
              <div className="space-y-1.5">
                <div className="flex items-center gap-x-2">
                  {isGeographic && standardizedCountry && (
                    <img
                      role="presentation"
                      alt={standardizedCountry.name}
                      src={getFlagSvgUrl(standardizedCountry.isoCountryCode)}
                      onContextMenu={(event) => event.preventDefault()}
                      onMouseDown={(event) => event.preventDefault()}
                      draggable={false}
                      className="h-4 w-4"
                    />
                  )}

                  <SimpleTextTooltip content={description} align="start" asChild={false}>
                    <p className="text-left text-sm font-bold text-gray-700 dark:text-slate-300 lg:line-clamp-1">
                      {description}
                    </p>
                  </SimpleTextTooltip>

                  {isPrimary && (
                    <span
                      className={classNames(
                        "rounded-xl px-1.5 text-xs font-bold tracking-wide",
                        "border border-pink-200 bg-pink-50 text-pink-600",
                        "dark:border-teal-800 dark:bg-teal-900 dark:text-teal-400",
                      )}
                    >
                      <FormattedMessage defaultMessage="Principal" id="SRhWkB" />
                    </span>
                  )}
                </div>

                {!isGeographic && (
                  <p className="space-x-2 font-mono text-xs font-semibold text-slate-500 dark:text-slate-300">
                    <FormattedNumber
                      style="currency"
                      notation="compact"
                      currency={currency}
                      minimumSignificantDigits={1}
                      maximumSignificantDigits={financialIndicator > 1e6 ? 2 : 4}
                      maximumFractionDigits={2}
                      minimumFractionDigits={2}
                      compactDisplay="short"
                      value={financialIndicator}
                    />
                  </p>
                )}
              </div>

              <div className="pl-8">
                <div className="flex items-center space-x-4">
                  <div>
                    <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                      <FormattedNumber
                        style="percent"
                        maximumFractionDigits={1}
                        value={ratioIndicator ? ratioIndicator / 100 : 0}
                      />
                    </span>
                  </div>

                  <span
                    aria-hidden={true}
                    className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700"
                    style={{ backgroundColor: lineColor }}
                  />
                </div>
              </div>
            </li>
          );
        })}

        {(restSegments.length > 0 || (otherSegment && totalRemainingRatio > 0)) && (
          <li className={classNames("space-y-2 py-4", "first:pt-0 last:pb-0")}>
            <div className={classNames("flex items-center justify-between gap-x-6")}>
              <div className="flex items-center space-x-2">
                {isGeographic ? (
                  <>
                    <Twemoji emoji="🌎" width={16} height={16} />

                    <div className="line-clamp-2 text-sm font-bold text-gray-700 dark:text-slate-300">
                      <SimpleTextTooltip
                        align="start"
                        content={intl.formatMessage({
                          defaultMessage: "Países não especificados pela empresa ou não padronizados.",
                          id: "RfxvDH",
                          description: "Tooltip do item 'Outros' do gráfico de receitas",
                        })}
                      >
                        <span>
                          <FormattedMessage
                            defaultMessage="Outros"
                            description="Complemento do gráfico de receitas"
                            id="8K4Qok"
                          />
                        </span>
                      </SimpleTextTooltip>
                    </div>
                  </>
                ) : (
                  <div className="line-clamp-2 text-sm font-bold text-gray-700 dark:text-slate-300">
                    <SimpleTextTooltip
                      align="start"
                      content={intl.formatMessage({
                        defaultMessage: "Receitas não especificadas pela empresa ou não padronizadas.",
                        id: "dBq2YU",
                        description: "Tooltip do item 'Demais Receitas' do gráfico de receitas",
                      })}
                    >
                      <span>
                        <FormattedMessage
                          defaultMessage="Demais Receitas"
                          description="Complemento do gráfico de receitas"
                          id="K+eiwc"
                        />
                      </span>
                    </SimpleTextTooltip>
                  </div>
                )}
              </div>

              <div className="pl-8">
                <div className="flex items-center space-x-4">
                  <div>
                    <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                      <FormattedNumber style="percent" maximumFractionDigits={1} value={totalRemainingRatio} />
                    </span>
                  </div>

                  <span
                    aria-hidden={true}
                    className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700"
                    style={{
                      backgroundColor: getColorThemeForIndex(limitedSegments.length + 1, theme as "light" | "dark")
                        .lineColor,
                    }}
                  />
                </div>
              </div>
            </div>
          </li>
        )}

        {!isGeographic && (
          <li className={classNames("space-y-2 py-4", "first:pt-0 last:pb-0")}>
            {hasSubTotal && (
              <div className={classNames("flex items-center justify-between gap-x-6")}>
                <div className="space-y-1.5">
                  <p className="line-clamp-2 text-sm font-bold text-gray-700 dark:text-slate-300">
                    Subtotal ({limitedSegments.length})
                  </p>
                </div>

                <div className="pl-8">
                  <p className="font-mono text-sm font-bold text-slate-600 dark:text-slate-300">
                    <FormattedNumber
                      style="currency"
                      notation="compact"
                      currency={currency}
                      minimumSignificantDigits={1}
                      maximumSignificantDigits={4}
                      maximumFractionDigits={2}
                      minimumFractionDigits={2}
                      compactDisplay="short"
                      value={sum(limitedSegments.map((node) => node.financialIndicator))}
                    />
                  </p>
                </div>
              </div>
            )}

            <div className={classNames("flex items-center justify-between gap-x-6")}>
              <div className="flex items-center space-x-1.5">
                <p className="line-clamp-2 text-sm font-bold text-gray-700 dark:text-slate-300">
                  {hasSubTotal ? `Total (${filteredSegments.length})` : "Total"}
                </p>

                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircleIcon className="h-4 w-4" />
                    </TooltipTrigger>

                    <TooltipContent
                      className="w-80 rounded-lg bg-gray-600 p-2 font-aeonik text-xs dark:bg-slate-700 dark:text-slate-300"
                      side="top"
                    >
                      <span className="font-bold tracking-wider text-white">
                        <FormattedMessage
                          defaultMessage="O cálculo dos % não considera valores negativos na Receita como eliminações ou descontos, por isso o valor total acima pode ser diferente da tabela fundamentalista, além de pequenos arredondamentos nos valores."
                          id="HKM/8P"
                          description="Conteúdo do tooltip de ajuda da Receita por negócio"
                        />
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="pl-8">
                <p className="font-mono text-sm font-bold text-slate-600 dark:text-slate-300">
                  <FormattedNumber
                    style="currency"
                    notation="compact"
                    currency={currency}
                    minimumSignificantDigits={1}
                    maximumSignificantDigits={4}
                    maximumFractionDigits={2}
                    minimumFractionDigits={2}
                    compactDisplay="short"
                    value={sum(
                      segments.map((node) => {
                        const financialIndicatorHistory = (node.financialIndicator?.history || []).find((candidate) => {
                          return getYear(parseISO(candidate.periodEndingDate)) === forYear;
                        });

                        return financialIndicatorHistory?.value || 0;
                      }),
                    )}
                  />
                </p>
              </div>
            </div>
          </li>
        )}
      </ul>

      {financialSegments.periodEndingDate && false && (
        <p className="pt-2 text-xs font-medium text-gray-500">
          <FormattedMessage
            defaultMessage="Relatório de {year}"
            id="BoGyKM"
            values={{
              year: getYear(parseISO(financialSegments.periodEndingDate)),
            }}
          />
        </p>
      )}
    </div>
  );
}

function getFlagSvgUrl(isoCountryCode: string) {
  return `https://s.fndm.to/svg/common/flags/${isoCountryCode}.svg`;
}
